import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';

import { useAuth } from './contexts/AuthContext.tsx';
import Layout from './components/Layout.tsx';
import LoginPage from './pages/LoginPage.tsx';
import RegisterPage from './pages/RegisterPage.tsx';
import DashboardPage from './pages/DashboardPage.tsx';
import CustomersPage from './pages/CustomersPage.tsx';
import ProjectsPage from './pages/ProjectsPage.tsx';
import SurveysPage from './pages/SurveysPage.tsx';
import DocumentsPage from './pages/DocumentsPage.tsx';
import ProjectDetailPage from './pages/ProjectDetailPage.tsx';
import CustomerDetailPage from './pages/CustomerDetailPage.tsx';
import SurveyDetailPage from './pages/SurveyDetailPage.tsx';
import ProfilePage from './pages/ProfilePage.tsx';
import InventoryPage from './pages/InventoryPage.tsx';
import QuotePage from './pages/QuotePage.tsx';
import QuoteCalculatorPage from './pages/QuoteCalculatorPage.tsx';
import QuoteRequestsPage from './pages/QuoteRequestsPage.tsx';
import RequestQuotePage from './pages/RequestQuotePage.tsx';
import CustomerPortalPage from './pages/CustomerPortalPage.tsx';
import LoadingScreen from './components/LoadingScreen.tsx';
import NotFoundPage from './pages/NotFoundPage.tsx';

// Protected Route component
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRoles = [] 
}) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has required role
  if (requiredRoles.length > 0 && user && !requiredRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Public Route component (redirect if already authenticated)
interface PublicRouteProps {
  children: React.ReactNode;
}

const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const { loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Routes>
        {/* Fully public — no layout, no auth */}
        <Route path="/request-quote" element={<RequestQuotePage />} />

        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } 
        />
        <Route 
          path="/register" 
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          } 
        />

        {/* Protected routes with layout */}
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          {/* Redirect root to dashboard */}
          <Route index element={<Navigate to="/dashboard" replace />} />
          
          {/* Dashboard - available to all authenticated users */}
          <Route path="dashboard" element={<DashboardPage />} />
          
          {/* Profile - available to all authenticated users */}
          <Route path="profile" element={<ProfilePage />} />
          
          {/* Customers - managers and surveyors only */}
          <Route 
            path="customers" 
            element={
              <ProtectedRoute requiredRoles={['manager', 'surveyor']}>
                <CustomersPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="customers/:id" 
            element={
              <ProtectedRoute requiredRoles={['manager', 'surveyor']}>
                <CustomerDetailPage />
              </ProtectedRoute>
            } 
          />
          
          {/* Projects - available to all authenticated users */}
          <Route path="projects" element={<ProjectsPage />} />
          <Route path="projects/:id" element={<ProjectDetailPage />} />
          <Route path="quotes/:id" element={<QuotePage />} />

          {/* Quote calculator + requests — manager only */}
          <Route
            path="quotes/calculator"
            element={
              <ProtectedRoute requiredRoles={['manager', 'surveyor']}>
                <QuoteCalculatorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="quote-requests"
            element={
              <ProtectedRoute requiredRoles={['manager']}>
                <QuoteRequestsPage />
              </ProtectedRoute>
            }
          />

          {/* Customer portal */}
          <Route
            path="my-quotes"
            element={
              <ProtectedRoute requiredRoles={['customer']}>
                <CustomerPortalPage />
              </ProtectedRoute>
            }
          />
          
          {/* Surveys - available to all authenticated users */}
          <Route path="surveys" element={<SurveysPage />} />
          <Route path="surveys/:id" element={<SurveyDetailPage />} />
          
          {/* Documents - available to all authenticated users */}
          <Route path="documents" element={<DocumentsPage />} />

          {/* Inventory - manager only */}
          <Route
            path="inventory"
            element={
              <ProtectedRoute requiredRoles={['manager']}>
                <InventoryPage />
              </ProtectedRoute>
            }
          />

          {/* 404 page for protected routes */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* 404 page for public routes */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Box>
  );
};

export default App;
